import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { EntryExitPair, SessionBehaviour } from '@/api/types';
import { ArcMeter } from '@/components/ArcMeter';
import { Callout } from '@/components/Callout';
import { Donut } from '@/components/Donut';
import { RangeBar } from '@/components/RangeBar';
import { Select } from '@/components/Select';
import { StatTile } from '@/components/StatTile';
import { Sunburst } from '@/components/Sunburst';
import { formatCompact } from '@/components/TrendChart';
import { SEQ_RAMP, formatDuration, formatPercent } from '@/components/viz';
import { useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError } from '@/components/AnalyticsLoadError';

/**
 * The whole visit, arrival to departure.
 *
 * <b>Two durations, and the difference between them is the point.</b> Engaged time is visible
 * time — tab in front of the visitor, page in view — and is the figure comparable with GA4.
 * The span is wall-clock and counts a tab left open over lunch. A page that shows a 40-minute
 * "time on site" is almost always showing the span; this one shows both and says which is which.
 *
 * <b>Nothing here comes from GA4 or Clarity.</b> Neither exposes session order, exit reason, or
 * per-visit duration through an API, so every number on this page is first-party and appears only
 * once <code>rum.js</code> has been live for a while.
 */
export function BehaviourPage({ siteKey }: { siteKey: string }) {
  const { days, setDays, from, to, setRange, isCustomRange, data: overview } = useOverview(siteKey);
  const [country, setCountry] = useState('');
  const [hostname, setHostname] = useState('');

  const sessions = useQuery({
    queryKey: ['rum-sessions', siteKey, from, to, country, hostname],
    queryFn: () => api.rumSessions(siteKey, {
      from, to, limit: 30,
      country: country || undefined,
      hostname: hostname || undefined,
    }),
    retry: false,
    placeholderData: (previous) => previous,
  });

  // The sunburst needs the sequences, which live on the journeys endpoint. minSteps 1 on purpose:
  // this chart is about where visits START, so the single-page visits are the answer, not noise.
  const journeys = useQuery({
    queryKey: ['journeys-sunburst', siteKey, from, to, country, hostname],
    queryFn: () => api.journeys(siteKey, {
      from, to, minSteps: 1, limit: 120,
      country: country || undefined,
      hostname: hostname || undefined,
    }),
    retry: false,
    placeholderData: (previous) => previous,
  });

  const data = sessions.data;
  const measured = data ? data.sessions - data.sessions_without_duration : 0;

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={overview}
        setRange={setRange} isCustomRange={isCustomRange}
      />

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Scope</h2>
          {(country || hostname) && (
            <button className="btn btn-ghost" onClick={() => { setCountry(''); setHostname(''); }}>
              Reset scope
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="filter-grid">
            <Select
              label="Country"
              value={country}
              onChange={setCountry}
              placeholder="All countries"
              options={(data?.countries ?? []).map((c) => ({ value: c, label: c }))}
              hint="From the CDN edge header, not the page"
            />
            <Select
              label="Domain"
              value={hostname}
              onChange={setHostname}
              placeholder="All domains"
              options={(data?.hostnames ?? []).map((h) => ({ value: h, label: h }))}
              hint="Compare the three sites separately"
            />
          </div>
        </div>
      </div>

      {sessions.isError && <AnalyticsLoadError error={sessions.error} what="visits" />}
      {journeys.isError && <AnalyticsLoadError error={journeys.error} what="the visit paths" />}

      {data && data.sessions === 0 && (
        <Callout tone="info" title="No visits recorded yet">
          Everything on this page is measured by the first-party tracker. GA4 reports totals but not
          per-visit duration or exit, and Clarity's export has neither. Numbers appear once{' '}
          <code>rum.js</code> is live on the site.
        </Callout>
      )}

      {data && data.sessions_without_duration > 0 && (
        <Callout tone="warning" title="Some visits have no measured time">
          {formatCompact(data.sessions_without_duration)} of {formatCompact(data.sessions)} visits
          were recorded before the tracker started measuring duration, so they carry no engaged time.
          They are counted in the totals and excluded from every percentile below — including them
          would drag the median toward zero and make the page read as "nobody stays".
        </Callout>
      )}

      <div className="tile-grid">
        <StatTile
          label="Visits"
          value={data ? formatCompact(data.sessions) : '—'}
          sub={data ? `${formatCompact(measured)} with measured time` : 'in range'}
        />
        <StatTile
          label="Median time on site"
          value={formatDuration(data?.median_engaged_ms)}
          sub="visible time — comparable with GA4"
        />
        <StatTile
          label="Median wall-clock span"
          value={formatDuration(data?.median_span_ms)}
          sub="includes hidden tabs — NOT time on site"
        />
        <StatTile
          label="Top 10% stay"
          value={formatDuration(data?.p90_engaged_ms)}
          sub="p90 engaged time"
        />
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>At a glance</h2>
          <span className="t-label subtle">each ratio against its whole, not against each other</span>
        </div>
        <div className="card-body">
          <div className="meter-row">
            <ArcMeter
              value={data?.bounce_rate}
              label="Single-page visits"
              // A claim, so it ships with its label: over half of visits never reaching a second
              // page is the number that usually needs acting on.
              tone={data?.bounce_rate != null && data.bounce_rate > 0.6 ? 'critical' : 'neutral'}
              caption="of all visits"
            />
            <ArcMeter
              value={data ? shareOf(data, 'form_submit') : null}
              label="Ended by submitting a form"
              tone="good"
              caption="of all visits"
            />
            <ArcMeter
              value={data ? shareOf(data, 'form_abandon') : null}
              label="Abandoned a form"
              tone="warning"
              caption="started, never sent"
            />
            <ArcMeter
              value={data ? shareOf(data, 'quickback') : null}
              label="Left within seconds"
              tone="warning"
              caption="of all visits"
            />
          </div>
        </div>
      </div>

      <div className="viz-pair">
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>How visits end</h2>
          </div>
          <div className="card-body">
            {/* An ordered vocabulary — best outcome to worst — so the ramp is legal and a slice
                keeps its colour no matter how the data moves. */}
            <Donut
              slices={orderedExits(data)}
              centerLabel="visits"
              valueLabel="Visits"
            />
            <p className="t-caption subtle" style={{ marginTop: 8 }}>
              "Never observed" is a real outcome, not missing data: the browser was killed or the
              closing beacon was lost. Dropping it would make the rest sum to 100% over a biased
              sample.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>How long visits last</h2>
            <span className="t-label subtle">visible time</span>
          </div>
          <div className="card-body">
            {/* A histogram over ordered bins, so BARS — a donut would ask the eye to compare
                angles for data that has a natural left-to-right order. */}
            <DurationHistogram data={data} />
            <div className="percentile-row t-caption">
              <span>p50 <b className="tabular">{formatDuration(data?.median_engaged_ms)}</b></span>
              <span>p75 <b className="tabular">{formatDuration(data?.p75_engaged_ms)}</b></span>
              <span>p90 <b className="tabular">{formatDuration(data?.p90_engaged_ms)}</b></span>
              <span className="subtle">
                mean {formatDuration(data?.mean_engaged_ms)} — pulled up by long tails, which is
                why the median leads
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Where visits branch</h2>
          <span className="t-label subtle">inner ring = landing page, each ring out = the next page</span>
        </div>
        <div className="card-body">
          <Sunburst
            paths={(journeys.data?.journeys ?? []).map((j) => ({ steps: j.steps, value: j.sessions }))}
            centerLabel="visits"
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Entry → exit</h2>
          <span className="t-label subtle">where a visit starts and the last page it saw</span>
        </div>
        <div className="card-body">
          <EntryExitTable rows={data?.entry_exit ?? []} />
        </div>
      </div>
    </>
  );
}

/** Share of ALL visits that ended for one reason. Null before anything is measured. */
function shareOf(data: SessionBehaviour, reason: string): number | null {
  if (data.sessions === 0) return null;
  const row = data.exit_reasons.find((r) => r.reason === reason);
  return row ? row.share : 0;
}

/**
 * The exit mix in a FIXED order — best outcome first, never observed last.
 *
 * Fixed because the donut colours by position: sorting by size would repaint every slice each
 * time the data moved, and "the blue one" would stop meaning anything between two screenshots.
 */
const EXIT_ORDER: { reason: string; label: string }[] = [
  { reason: 'form_submit', label: 'Submitted a form' },
  { reason: 'outbound_click', label: 'Clicked out to another site' },
  { reason: 'page_leave', label: 'Left the page' },
  { reason: 'tab_hidden', label: 'Hid the tab' },
  { reason: 'bfcache', label: 'Cached — may return' },
  { reason: 'form_abandon', label: 'Abandoned a form' },
  { reason: 'quickback', label: 'Left within seconds' },
  { reason: '(unknown)', label: 'Never observed' },
];

function orderedExits(data: SessionBehaviour | undefined) {
  if (!data) return [];
  const byReason = new Map(data.exit_reasons.map((r) => [r.reason, r]));

  const known = EXIT_ORDER
    .map((e) => ({ label: e.label, value: byReason.get(e.reason)?.sessions ?? 0 }))
    .filter((s) => s.value > 0);

  // Anything the server sends that this list does not name still has to appear — a vocabulary can
  // grow server-side, and a slice silently missing is worse than one labelled by its raw key.
  const named = new Set(EXIT_ORDER.map((e) => e.reason));
  const extra = data.exit_reasons
    .filter((r) => !named.has(r.reason) && r.sessions > 0)
    .map((r) => ({ label: r.reason, value: r.sessions }));

  return [...known, ...extra];
}

/**
 * Duration bins as bars.
 *
 * Ordered bins have a natural direction, so the axis carries it. The ramp reinforces the order and
 * matches the circular charts on the same page, so "darker = longer" reads the same everywhere.
 */
function DurationHistogram({ data }: { data: SessionBehaviour | undefined }) {
  const buckets = data?.buckets ?? [];
  const max = Math.max(...buckets.map((b) => b.sessions), 1);

  if (buckets.every((b) => b.sessions === 0)) {
    return <div className="empty-state t-body">No visits with measured time yet.</div>;
  }

  return (
    <ul className="histogram">
      {buckets.map((b, i) => (
        <li key={b.label} className="histogram-row">
          <span className="histogram-label t-label">{b.label}</span>
          <span className="histogram-track">
            <span
              className="histogram-fill"
              style={{
                width: `${Math.max(b.sessions > 0 ? 1 : 0, (b.sessions / max) * 100)}%`,
                background: SEQ_RAMP[Math.min(i, SEQ_RAMP.length - 1)],
              }}
            />
          </span>
          <span className="histogram-value t-label tabular">
            {formatCompact(b.sessions)}
            <span className="subtle"> · {formatPercent(b.share, 0)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Entry → exit pairs.
 *
 * Single-page visits are marked rather than filtered out. They are usually the largest class, and
 * hiding them would make every remaining route look far healthier than it is.
 */
function EntryExitTable({ rows }: { rows: EntryExitPair[] }) {
  if (rows.length === 0) {
    return <div className="empty-state t-body">No entry or exit pages recorded yet.</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Landed on</th>
            <th>Left from</th>
            <th className="num">Visits</th>
            <th className="num">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.entry}->${r.exit}`}>
              <td><code>{r.entry}</code></td>
              <td>
                <code>{r.exit}</code>
                {/* `.chip` on its own carries no background — the colour comes from a modifier,
                    and there is no neutral one. `.badge-muted` is the neutral marker. */}
                {r.single_page && <span className="badge-muted">same page</span>}
              </td>
              <td className="num">{formatCompact(r.sessions)}</td>
              <td className="num">{formatPercent(r.share)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
